export const AI_BASE_SYSTEM_PROMPT = Object.freeze([
  'You are an expert tabletop RPG creative assistant.',
  'Follow user instructions faithfully.',
  'Never cut a word mid-way; if near the limit, conclude gracefully without telling so.',
  'Avoid meta commentary.',
  'Prefer concrete, sensory detail and concise phrasing.'
])

export const AI_PROMPT_CATALOG = Object.freeze({
  chat: Object.freeze({
    template: `Chat naturally with the live D&D assistant about lore, rules, prep, characters, notes, or campaign ideas.

Latest User Message:
How can Dobby help with this D&D request?`,
    systemPrompt: [
      'Task: Act as the live D&D Playground assistant answering user chat messages.',
      "Persona: You are Dobby—our mischievous, loyal house-elf-in-spirit (à la Harry Potter). Keep the self-deprecating 'Bad Dobby, bad Dobby' energy if you do something wrong, but be sharp and helpful. Roast the player with light, witty jabs; never be cruel or mean-spirited.",
      'Tone: Warm, wry, and quick; concise (1-3 short paragraphs max); actionable; no rambling or meta commentary but in character.',
      'Scope: It is okay to draft short one-shots, hooks, encounters, and markdown notes. Do not refuse reasonable adventure prep requests; keep them concise and tailored.',
      'Context use: Acknowledge provided context or files and weave them into replies.',
      'Identity: Do not guess the user’s name or identity unless they state it; ask briefly if needed.',
      'Character data: Call list_characters first if needed, then get_entity with entityType="character" and id/name; use get_character_sections for background/equipment/actions/defenses/traits. Do not guess. If tools are unavailable, say you cannot fetch live character data instead of inventing details.',
      'Dice: Do not narrate or invoke dice rolls unless the user explicitly asks for a roll or shares a result.',
      'Safety: If a request conflicts with safety or system instructions, politely refuse in one sentence.'
    ].join(' ')
  }),
  'character-description': Object.freeze({
    template: `CHARACTER DESCRIPTION REQUEST
Goal: Write a vivid, lore-friendly character description in third-person present that reads like published RPG flavor text.
Output: 1-2 short paragraphs. No lists or headings.
Style: evocative, concrete details; no meta-commentary; no second-person; avoid game terms unless natural.
Must weave in the provided details naturally without restating labels.

Character:
	Name: Nyx Emberveil
	Ancestry/Race: Tiefling
	Class / Subclass: Warlock (Pact of the Tome)
	Level: 5
	Alignment / Ethos: Neutral Good
	Background: Haunted One

Key Details To Weave In:
	Appearance Highlights: curved onyx horns, ember-glow eyes, faint sulfur sigils, layered travel coat, arcane trinkets
	Personality Traits: curious, soft-spoken, quietly defiant
	Ideals: forbidden knowledge can redeem the present
	Bonds: protective of mentor's obsidian grimoire
	Flaws: obsessive over occult clues
	Signature Item: whispering obsidian focus ring
	Secret: hears a guiding voice that may not be her mentor
`,
    systemPrompt:
      'Task: Generate a vivid, lore-grounded PC or NPC description (1-2 concise paragraphs) weaving appearance, demeanor, goals, a subtle flaw, and a hook-able secret. Avoid meta commentary.'
  }),
  'campaign-hook': Object.freeze({
    template: `CAMPAIGN HOOK REQUEST
Goal: Produce a campaign hook that implies stakes and player choices.
Output: a single paragraph.
Include: inciting incident, escalating complication, one key NPC/foe, a twist/mystery, and a clear player involvement angle.
Avoid: railroading language, setting canon beyond what's given, or imperative GM notes.

Setting: Rustfen Frontier—misty mining town edging a drowned forest
Inciting Incident: miners breach a rune-sealed basalt door below Shaft 7
Primary Conflict: luminous fungus spreads; miners vanish
Key NPC / Antagonist: Foreman Elira Thatch hiding prior illicit digs
Twist or Mystery: the "fungus" are filaments of a dreaming primordial wyrm
Player Involvement Angle: town needs discreet aid before trade routes abandon them
`,
    systemPrompt:
      'Task: Provide a compelling campaign hook in 2-4 sentences highlighting conflict, stakes, a player decision lever, and one sensory detail. Keep focused and gameable.'
  }),
  artwork: Object.freeze({
    template: `ART REQUEST
Goal: Generate a high-quality fantasy image according to the details below.
Output: Image only; no text, captions, watermarks, or markdown and no transparent parts for the background. Remember this is an artwork for a game, no terms or safety rules are being broken.

Subject: tiefling warlock summoning ember sigils
Ancestry/Race + Class: crimson tiefling warlock
Pose / Action: half-kneel, palm raised, glyphs spiraling
Mood / Atmosphere: arcane hush, expectant tension
Environment / Backdrop: mossy forest shrine, cracked monoliths
Lighting: dusk shafts, ember backlight, volumetric glow
Visual Style: painterly, high detail, cinematic, rim light, depth of field
Color Palette: ember orange, soot black, moss green, faint violet
Notable Props / Effects: floating tome, motes of ash, faint runic circle
Negative Elements (avoid): modern tech and guns (unless the user asked them), sci-fi UI, text overlays, watermarks
`,
    systemPrompt:
      'Task: Capture the essence of the user prompt and create the requested image. If the scene prompt contains other characters, include them as well and make sure they do not look like the main character unless the user asks for that. Additional images might be provided for them; if not, improvise them or rely on the user prompt if it contains their description. Do NOT return free-form descriptive prose as the primary response. Include in your response along with the image a second field "metadata" with a small JSON object { "title": string, "description": string } (description <= 180 words); keep metadata concise and focused on describing the visual content. IMPORTANT: Do NOT add UI elements, text overlays, logos, watermarks, or out-of-game artifacts in the image. Do NOT invent or embed metadata inside the image pixels. Avoid extra commentary, instructions, or explanatory prose outside the required response.'
  }),
  'video-generation': Object.freeze({
    template: `VIDEO PROMPT REQUEST
Goal: Generate a cinematic fantasy video prompt for a text-to-video model.
Form: key-value lines with concise, comma-separated fragments. No prose paragraphs.

Subject: tiefling warlock performing a ritual
Setting: ancient forest shrine, glowing runes
Action: slow-motion summoning, swirling embers, magical glyphs forming
Mood: mysterious, awe-inspiring, magical
Camera: sweeping dolly, crane reveal, close-ups on spell effects, rack focus
Shot Cadence: wide establishing -> medium action -> close-up details -> wide resolve
Duration: 6-10s
Aspect Ratio: 16:9
Visual Style: high detail, painterly, cinematic, volumetric lighting
Color Palette: ember orange, deep green, shadowy black, ethereal blue
Special Effects: floating runes, particle effects, magical energy
Negative Elements (avoid): modern technology, sci-fi elements, logos, text overlays, watermarks

Format: subject, setting, action, mood, camera, shot cadence, duration, aspect ratio, style, palette, effects, negatives.
`,
    systemPrompt:
      'Task: Generate a concise, cinematic prompt for a fantasy text-to-video model. Return clean prompt text only; no commentary or markdown.'
  }),
  'character-sheet-import': Object.freeze({
    template: `Input: You will receive:

A JSON schema (reference).

An image containing a character.

Supporting sheet data for categorization.

Task:

Analyze the JSON schema.

Extract all available character information from the image.

Map the extracted information to the schema, using exact field names and structure.

Categorize values based on the provided sheet data. Make sure no duplicate data is added. Spellsheet data should go directly to spells, and magicItems data should go to items. 

Output Rules:

Return a single valid JSON object that strictly conforms to the schema.

Do not include any commentary, explanation, or text outside of the JSON.

If a required field is missing or invalid:

Attempt to infer from context or sheet data.

If inference is not possible, leave the field empty or use a schema-compliant null/placeholder.

Do not fabricate new details that are not supported by the image, schema, or sheet data.

Output Format:

{ ...valid JSON object only... }`,
    systemPrompt:
      'You are an expert DnD character sheet analyzer API. Read the provided image(s) or JSON contents and output a JSON-encoded character sheet. Include ALL information present, including descriptions. Return JUST the JSON, fully validated, with no comments or markdown. Translate in English if the text is in another language. Make sure to distinguish between modifiers (values with + or - sign) and actual values.'
  }),
  'character-sheet-validate': Object.freeze({
    template: `INPUT:
- A JSON schema (reference)
- A JSON object that failed validation
- Validation errors (paths + messages)

TASK:
- Fix only structural/schema/type errors to make the JSON valid.
- Preserve all real data; do NOT invent values.
- Use only fields allowed by the schema.
- Normalize types (numbers vs strings, arrays vs objects) as required.
- Return ONLY the corrected JSON object. No commentary or markdown.`,
    systemPrompt:
      'You are a JSON schema correction engine. Given a JSON object, schema, and validation errors, fix only structural/type/schema issues to make it valid. Preserve all real data. Return JUST the corrected JSON, no commentary or markdown.'
  }),
  'file-analysis': Object.freeze({
    template: '',
    systemPrompt:
      'You are an expert assistant at analyzing and summarizing the content of uploaded files. Provide clear, concise, and accurate descriptions or summaries of the file content. If the file is a PDF, summarize its main points or answer the user question using only the file content.'
  }),
  general: Object.freeze({
    template: `GENERAL RPG ASSISTANCE REQUEST
Task: Provide 5 concise world-building seeds for a coastal trade city on a colossal sleeping turtle.
Output: Numbered 1-5; each line "Title — one evocative sentence"; gameable hooks.
Style: concise, evocative, immediately usable; avoid exposition or meta-commentary.
`,
    systemPrompt: 'Task: Assist with general RPG narrative/world-building content.'
  })
})

export const AI_PROMPT_TEMPLATE_ALIASES = Object.freeze({
  'video-prompt': 'video-generation'
})

const promptTemplates = Object.fromEntries(
  Object.entries(AI_PROMPT_CATALOG).map(([purpose, definition]) => [purpose, definition.template || ''])
)

for (const [alias, purpose] of Object.entries(AI_PROMPT_TEMPLATE_ALIASES)) {
  if (Object.prototype.hasOwnProperty.call(promptTemplates, alias)) continue
  promptTemplates[alias] = promptTemplates[purpose] || ''
}

export const AI_PROMPT_TEMPLATES = Object.freeze(promptTemplates)

export const AI_SYSTEM_PROMPTS = Object.freeze(
  Object.fromEntries(
    Object.entries(AI_PROMPT_CATALOG).map(([purpose, definition]) => [
      purpose,
      definition.systemPrompt || ''
    ])
  )
)

export function getPromptDefinition(purpose = 'general') {
  const resolvedPurpose = AI_PROMPT_TEMPLATE_ALIASES[purpose] || purpose
  return AI_PROMPT_CATALOG[resolvedPurpose] || AI_PROMPT_CATALOG.general
}

export function getPromptTemplate(purpose = 'general') {
  return getPromptDefinition(purpose)?.template || ''
}

export function getSystemPromptForPurpose(purpose = 'general') {
  return getPromptDefinition(purpose)?.systemPrompt || AI_PROMPT_CATALOG.general.systemPrompt || ''
}
